import styled from 'styled-components';

// ─── Container ───────────────────────────────────────────────────
export const AiContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: 600px) {
        padding: 0;
    }
`;

export const AiTitle = styled.h1`
    margin: 0 0 0.25rem 0;
    color: ${(props) => props.theme.text};
    font-size: ${(props) => props.theme.type.pageTitle};
    font-weight: 800;
`;

export const AiSubtitle = styled.p`
    color: ${(props) => props.theme.text2};
    font-size: ${(props) => props.theme.type.caption};
    margin: 0 0 1.5rem 0;
`;

// ─── Card ─────────────────────────────────────────────────────────
export const AiCard = styled.div`
    background: ${(props) => props.theme.surface};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: ${(props) => props.theme.radius.md};
    box-shadow: ${(props) => props.theme.shadowSm};
    margin-bottom: 1rem;
    padding: 1.25rem;

    /* Évite de cumuler la marge du dernier enfant avec le padding du bas */
    > *:last-child {
        margin-bottom: 0;
    }

    @media (max-width: 600px) {
        padding: 1rem;
    }
`;

// ─── Form ────────────────────────────────────────────────────────
export const AiForm = styled.form`
    display: flex;
    gap: 0.75rem;
    margin-bottom: 1rem;
`;

export const AiInput = styled.input`
    flex: 1;
    padding: 0.6rem 0.9rem;
    border: 1px solid ${(props) => props.theme.borderStrong};
    border-radius: ${(props) => props.theme.radius.sm};
    font-size: ${(props) => props.theme.type.body};
    font-family: inherit;
    background-color: ${(props) => props.theme.surface};
    color: ${(props) => props.theme.text};
    transition: border-color 0.12s ease;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${(props) => props.theme.accent};
    }

    &::placeholder {
        color: ${(props) => props.theme.text3};
    }

    &:disabled {
        background: ${(props) => props.theme.surface2};
        cursor: not-allowed;
    }
`;

export const AiButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem 1.25rem;
    font-size: ${(props) => props.theme.type.body};
    font-family: inherit;
    font-weight: 700;
    color: ${(props) => props.theme.onAccent};
    background: ${(props) => props.theme.accent};
    border: 1px solid transparent;
    border-radius: ${(props) => props.theme.radius.sm};
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.12s ease;

    &:hover:not(:disabled) {
        background: ${(props) => props.theme.accentHover};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

// ─── Suggestions ─────────────────────────────────────────────────
export const AiHistory = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
`;

export const AiHistoryChip = styled.button`
    background: ${(props) => props.theme.surface2};
    color: ${(props) => props.theme.text2};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: ${(props) => props.theme.radius.pill};
    padding: 0.3rem 0.9rem;
    font-size: ${(props) => props.theme.type.caption};
    font-family: inherit;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
        background: ${(props) => props.theme.surface3};
        color: ${(props) => props.theme.text};
    }
`;

// ─── Résultats ───────────────────────────────────────────────────
export const AiResultMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
`;

export const AiDescription = styled.p`
    font-weight: 600;
    color: ${(props) => props.theme.text};
    margin: 0;
    flex: 1;
`;

export const AiBadge = styled.span`
    background: ${(props) => props.theme.accentSoft};
    color: ${(props) => props.theme.accentText};
    padding: 0.2rem 0.6rem;
    border-radius: ${(props) => props.theme.radius.pill};
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
`;

export const AiExportButton = styled.button`
    background-color: transparent;
    border: 1px solid ${(props) => props.theme.border};
    border-radius: ${(props) => props.theme.radius.sm};
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    color: ${(props) => props.theme.text2};
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: ${(props) => props.theme.surface3};
        color: ${(props) => props.theme.text};
    }

    &:active:not(:disabled) {
        transform: scale(0.95);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

// ─── Requête générée ─────────────────────────────────────────────
export const AiQueryBlock = styled.div`
    margin-bottom: 1rem;
`;

export const AiQueryToggle = styled.button`
    width: 100%;
    text-align: left;
    padding: 0.5rem 0.75rem;
    background: ${(props) => props.theme.surface2};
    color: ${(props) => props.theme.text2};
    border: none;
    border-radius: ${(props) => props.theme.radius.sm};
    font-size: ${(props) => props.theme.type.caption};
    font-family: inherit;
    cursor: pointer;
    transition: background-color 0.12s ease;

    &:hover {
        background: ${(props) => props.theme.surface3};
    }
`;

export const AiQueryCode = styled.pre`
    margin: 0.35rem 0 0;
    padding: 0.75rem;
    background: ${(props) => props.theme.surface2};
    border: 1px solid ${(props) => props.theme.border};
    border-radius: ${(props) => props.theme.radius.sm};
    color: ${(props) => props.theme.text};
    font-size: 0.75rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-all;
`;

// ─── Tableau ──────────────────────────────────────────────────────
export const AiTableWrapper = styled.div`
    overflow-x: auto;
`;

export const AiTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: ${(props) => props.theme.type.table};

    th {
        background: ${(props) => props.theme.surface2};
        color: ${(props) => props.theme.text2};
        padding: 0.5rem 0.75rem;
        text-align: left;
        font-size: ${(props) => props.theme.type.label};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 1px solid ${(props) => props.theme.border};
        white-space: nowrap;
        font-weight: 700;
    }

    td {
        padding: 0.45rem 0.75rem;
        border-bottom: 1px solid ${(props) => props.theme.border};
        color: ${(props) => props.theme.text};
        max-width: 300px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    tr:last-child td {
        border-bottom: none;
    }

    tr:hover td {
        background: ${(props) => props.theme.surface3};
    }
`;

export const AiEmpty = styled.div`
    padding: 2rem;
    text-align: center;
    color: ${(props) => props.theme.text2};
`;

// ─── Insight ─────────────────────────────────────────────────────
export const AiInsight = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.75rem 1rem;
    background: ${(props) => props.theme.accentSoft};
    border-radius: ${(props) => props.theme.radius.sm};
    font-size: ${(props) => props.theme.type.body};
    color: ${(props) => props.theme.text};
    margin-bottom: 1rem;
    line-height: 1.5;

    i {
        color: ${(props) => props.theme.accent};
        font-size: 1rem;
        flex-shrink: 0;
        margin-top: 0.1rem;
    }
`;

// ─── Erreur ───────────────────────────────────────────────────────
export const AiError = styled.div`
    background: ${(props) => props.theme.dangerSoft};
    border: 1px solid transparent;
    border-radius: ${(props) => props.theme.radius.sm};
    padding: 0.75rem 1rem;
    font-size: ${(props) => props.theme.type.body};
    color: ${(props) => props.theme.danger};
    margin-bottom: 1rem;
`;

export const AiScopeBanner = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: ${(props) => props.theme.type.caption};
    font-weight: 700;
    color: ${(props) => props.theme.rssi};
    padding: 0.25rem 0.8rem;
    background: ${(props) => props.theme.rssiSoft};
    border-radius: ${(props) => props.theme.radius.pill};
    margin-bottom: 0.75rem;
    align-self: flex-start;

    i {
        font-size: 1rem;
        flex-shrink: 0;
    }
`;
